import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ProductPriceBlock, Rating } from '@codex/basics';
import { colors } from '@codex/themes';
import { Icon } from '@codex/icons';
import ShowDifferencesTableCell from './ShowDifferencesTableCell';
import ProductImageAndNameCell from './ProductImageAndNameCell';
import ChannelsAvailability from '../../ChannelsAvailability/ChannelsAvailability';
import AddToBasketButton from './AddToBasketButton';
import { getTranslation } from '../../../translations/translations';
import { getDefaultDeliveriesAndCollectionForProduct } from '../../../dataConverters/defaultChannelsAvailabilitiesConverter';


const StickyTable = styled.table`
  table-layout: fixed;
  z-index: 3;
  top: 68px;
  box-shadow: 0 2px 10px -4px rgba(0,0,0,0.75);
  background: ${colors.shades[7]};
  border-collapse: collapse;
  
  tr.highlighted {
    background: #b9e1fc;
  }

  a {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const TableCell = styled.td`
  border: 1px solid ${colors.shades[5]};
  padding: 5px 7px;
  vertical-align: top;
  text-align: left;
  width: 20%;
  overflow: hidden;
`;

const GreyTableCell = styled.td`
  background-color: ${colors.shades[6]}; 
  fontWeight: 'bold';
  border: 1px solid ${colors.shades[5]};
  padding: 5px 7px;
  vertical-align: top;
  text-align: left;
  width: 20%;
  overflow: hidden;
`;

const CrossIcon = styled(Icon)`
  height: 24px;
  width: 24px;
  color: ${colors.shades[3]};
 
`;

const OutOfStockLabel = styled.span`
  color: ${colors.shades[3]};
  font-size: 13px;
  vertical-align: middle;
`;

const AddToBasketErrorMessage = styled.div`
  color: red;
  padding-top: 20px;
  
`;


export default function TopInfoTable(
  { productInfoList, isShowDifferencesOnOffSwitchActive, onShowDifferencesOnOffSwitchClick, onRemoveButtonClick,
    addingProductToBasket, onAddToBasketButtonClick }) {

  return (
    <StickyTable>
      <tbody>
        {/* First Row */}
        <tr>
          <TableCell style={{ padding: '15px' }}>
            <ShowDifferencesTableCell
              active={isShowDifferencesOnOffSwitchActive}
              onShowDifferencesOnOffSwitchClick={onShowDifferencesOnOffSwitchClick}
            />
          </TableCell>
          {
            productInfoList.map((productData, productIndex) => (
              <TableCell
                style={{ paddingTop: '0' }}
                key={productData.productId}
              >
                <ProductImageAndNameCell
                  productImgUrl={productData.productImageUrl}
                  productTitle={productData.productLabel}
                  productLink={productData.productLink}
                  onRemoveButtonClick={(e) => {
                    e.preventDefault();
                    // removing is not possible if a product is being added to basket
                    if (!addingProductToBasket || !addingProductToBasket.inProgress) {
                      onRemoveButtonClick(productIndex);
                    }

                  }}
                />
              </TableCell>
            ))
          }
        </tr>
        {/* Price Row */}
        <tr>
          <GreyTableCell>
            {/* TODO: add to translations */}
            {'Price'}
          </GreyTableCell>
          {
            productInfoList.map((productData) => {
              const defaultChannelsAvailability = getDefaultDeliveriesAndCollectionForProduct(productData);
              const isOutOfStock =
                defaultChannelsAvailability.showEmailMeBack ||
                (typeof defaultChannelsAvailability.delivery !== 'undefined' &&
                  defaultChannelsAvailability.delivery.type === 'homeDeliveryUnavailable' &&
                  typeof defaultChannelsAvailability.inStore !== 'undefined' &&
                  defaultChannelsAvailability.inStore.type === 'collectInStoreUnavailable'
                );

              return (
                <TableCell
                  key={productData.productId}
                >
                  <ProductPriceBlock
                    outOfStock={isOutOfStock}
                    displayWasPriceRange
                    currentPrice={productData.price.currentPrice}
                    savingsPrice={productData.price.savingsPrice
                      ? `${getTranslation('savingsPriceTranslation')} ${productData.price.savingsPrice}`
                      : undefined}
                    wasPrice={productData.price.wasPrice
                      ? `${getTranslation('wasPriceTranslation')} ${productData.price.wasPrice}`
                      : undefined}
                    wasPriceFrom={productData.price.wasPriceFrom}
                    wasPriceTo={productData.price.wasPriceTo}
                    variant={'listing_list'}
                    mainProductWasPriceTranslation={getTranslation('mainProductWasPriceTranslation')}
                    webExclusivePriceTranslation={getTranslation('webExclusivePriceTranslation')}
                    perMonthTranslation={getTranslation('perMonthTranslation')}
                    fromDateTranslation={getTranslation('fromDateTranslation')}
                    toDateTranslation={getTranslation('toDateTranslation')}
                  />
                </TableCell>
              );
            })
          }
        </tr>
        {/* Add To Basket row */}
        <tr>
          <GreyTableCell>
            {/* TODO: add to translations */}
            {'Add to basket'}
          </GreyTableCell>
          {
            productInfoList.map((productData) => {
              const defaultChannelsAvailability = getDefaultDeliveriesAndCollectionForProduct(productData);
              const isOutOfStock =
                defaultChannelsAvailability.showEmailMeBack ||
                (typeof defaultChannelsAvailability.delivery !== 'undefined' &&
                  defaultChannelsAvailability.delivery.type === 'homeDeliveryUnavailable' &&
                  typeof defaultChannelsAvailability.inStore !== 'undefined' &&
                  defaultChannelsAvailability.inStore.type === 'collectInStoreUnavailable'
                );

              return (
                <TableCell key={productData.productId}>
                  {isOutOfStock
                    ? (
                      <>
                        <CrossIcon icon={'Cross'} />
                        <OutOfStockLabel>
                          {/* TODO: add to translations */}
                          Sorry this item is out of stock
                        </OutOfStockLabel>
                      </>
                    )
                    : (
                      <AddToBasketButton
                        buttonLabel={'Add to basket'} // TODO: add to translations
                        download={productData.isDownload}
                        disabled={addingProductToBasket && addingProductToBasket.inProgress}
                        showSpinner={addingProductToBasket && addingProductToBasket.inProgress &&
                        addingProductToBasket.productId === productData.productId}
                        onClick={(e) => {
                          e.preventDefault();
                          onAddToBasketButtonClick(productData.productId);
                        }}
                      />
                    )}
                  {
                    addingProductToBasket && addingProductToBasket.productId === productData.productId &&
                    addingProductToBasket.errorMessage && (
                      <AddToBasketErrorMessage>
                        {addingProductToBasket.errorMessage}
                      </AddToBasketErrorMessage>
                    )
                  }
                </TableCell>
              );
            })
          }
        </tr>
        {/* Availability row */}
        <tr>
          <GreyTableCell>
            {/* TODO: add to translations */}
            {'Availability'}
          </GreyTableCell>
          {
            productInfoList.map((productData) => (
              <TableCell
                key={productData.productId}
              >
                <ChannelsAvailability
                  id={productData.productId}
                  defaultChannelsAvailability={getDefaultDeliveriesAndCollectionForProduct(productData)}
                  setEmailMeBackProductID={() => {
                    window.location.href = `${productData.productLink}/#email-when-back-desktop`;
                  }}
                />
              </TableCell>

            ))
          }
        </tr>
        {/* Customer rating row */}
        <tr>
          <GreyTableCell>
            {/* TODO: add to translations */}
            {'Customer rating'}
          </GreyTableCell>
          {
            productInfoList.map((productData) => (
              <TableCell key={productData.productId}>
                <Rating
                  maxCount={5}
                  ratingValue={productData.customerReview.averageScore}
                  ratingCount={productData.customerReview.number}
                />
              </TableCell>
            ))
          }
        </tr>
      </tbody>
    </StickyTable>
  );
}


TopInfoTable.propTypes = {
  productInfoList: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string,
      productLabel: PropTypes.string,
      productImageUrl: PropTypes.string,
      price: PropTypes.shape({
        currentPrice: PropTypes.string.isRequired,
        savingsPrice: PropTypes.string,
        wasPrice: PropTypes.string,
        wasPriceFrom: PropTypes.string,
        wasPriceTo: PropTypes.string
      }).isRequired,
      isDownload: PropTypes.bool,
      productLink: PropTypes.string.isRequired,
      customerReview: PropTypes.shape({
        number: PropTypes.number.isRequired,
        averageScore: PropTypes.number.isRequired
      }).isRequired
    }).isRequired)
    .isRequired,
  isShowDifferencesOnOffSwitchActive: PropTypes.bool.isRequired,
  onShowDifferencesOnOffSwitchClick: PropTypes.func.isRequired,
  onRemoveButtonClick: PropTypes.func.isRequired,
  onAddToBasketButtonClick: PropTypes.func.isRequired,
  addingProductToBasket: PropTypes.shape({
    productId: PropTypes.string,
    inProgress: PropTypes.bool,
    errorMessage: PropTypes.string
  })
};

TopInfoTable.defaultProps = {
  addingProductToBasket: null
};
